<template>
<div class="form-input flex flex-col" :data-test="`${dataTest}__wrapper`" :data-invalid="(doNotBlock && !validationMessage) ? undefined : (isInvalid || undefined)">
	<!-- Number NIP -->
	<div class="form-input-group form-input-nip">
		<div :class="wrapperOutlineClass">
			<DropdownSelect v-model="modelValue.country" :Items="countriesByCode" :Disabled="(forceDisabled || isDisabled)" :data-test="`${dataTest}__dropdown`" @change="$emit('dirty')"/>
			<input
				:id="`${name}__input`"
				v-model="modelValue.value"
				class="!bg-transparent"
				type="text"
				placeholder="0000000000"
				:data-test="`${dataTest}__input`"
				:disabled="(forceDisabled || isDisabled)"
				@keydown="onKeyDown"
				@keyup="onKeyUp"
				@change="onInput"
			>
		</div>
	</div>
	<label :for="`${name}__input`" class="form-input-label" :class="{ 'is-required': !doNotBlock && !modelValue.isIndividual }" v-html="label || 'Numer NIP'"/>
	<div class="form-input-validation | mt-2 text-xs" :class="(forceDisabled || isDisabled) ? 'text-neutral-600' : 'text-danger'" v-html="(forceDisabled || isDisabled) ? validationMessages['NIP_DISABLED'] : validationMessage"/>

	<!-- Prowadzę nierejestrowaną działalność gospodarczą -->
	<div v-if="!doNotBlock" :class="{ 'hidden': !modelValue.isIndividual && (forceDisabled || isDisabled) }">
		<label class="form-input-checkbox | mt-2">
			<div class="input-control">
				<input v-model="modelValue.isIndividual" type="checkbox" @change="onInput"> <div class="i"/>
			</div>
			Prowadzę nierejestrowaną działalność gospodarczą
		</label>
	</div>
</div>
</template>

<script lang="ts">
import { mapState, mapStores } from 'pinia';
import { PropType } from 'vue';

import serverFetch from '@globals/ServerFetch';
import { useLocationStore } from '@globals/stores/location';
import { useUserStore } from '@globals/stores/user';
import { NipValue, NipValidationMessage } from '@globals/types/components/NipInput';

export default {
	name: 'NipInput',
	props: {
		modelValue: {
			type: Object as PropType<NipValue>,
			required: true,
		},
		name: {
			type: String,
			default: 'nipInput',
		},
		dataTest: {
			type: String,
			default: 'nip',
		},
		doNotBlock: {
			type: Boolean,
			default: false,
		},
		forceDisabled: {
			type: Boolean,
			default: false,
		},
		disableValidation: {
			type: Boolean,
			default: false,
		},
		label: {
			type: String,
			default: '',
		},
	},
	emits: [
		'update:modelValue',
		'dirty',
	],
	data() {
		const hotlineLink = '<a href="/konto/formularz-kontaktowy?subject=contactChangeNip" class="font-bold page-link text-info">Biurem obsługi Klienta</a>';

		return {
			isChanging: false,
			validationMessages: {
				NIP_TOO_LONG: 'Nieprawidłowa długość numeru NIP',
				NIP_WRONG_LENGTH: 'Nieprawidłowa długość numeru NIP',
				NIP_CONTAINS_NON_DIGIT: 'Numer NIP może zawierać tylko cyfry',
				NIP_WRONG_CHECKSUM: 'Nieprawidłowa suma kontrolna numeru NIP',
				NIP_EMPTY: 'Numer NIP nie może być pusty',
				NIP_DUPLICATED: `Numer NIP jest już przypisany do innego konta. Skontaktuj się z ${hotlineLink}, aby zweryfikować konto.`,
				NIP_DISABLED: `Aby zmienić NIP, skontaktuj się z ${hotlineLink}`,
				NIP_ERROR: 'Wystąpił błąd podczas sprawdzania numeru NIP',
				NIP_CONTAINS_ONLY_SAME_DIGITS: 'Nieprawidłowa suma kontrolna numeru NIP',
				NIP_NOT_EXISTS: 'Podany numer NIP nie istnieje w bazie GUS',
			},
			validationMessage: '',
			isDisabled: false,
		};
	},
	computed: {
		...mapState(useLocationStore, ['countriesByCode']),
		...mapStores(useUserStore),
		isInvalid() {
			return !!this.validationMessage || (!this.modelValue.isIndividual && !this.modelValue.value.length);
		},
		wrapperOutlineClass() {
			if (this.doNotBlock && !this.validationMessage) {
				return '';
			}

			if (this.isDisabled && !this.doNotBlock) {
				return '';
			}

			if (this.isInvalid) {
				return '!border !border-danger';
			}

			if (!this.validationMessage && this.modelValue.value.length && !this.isChanging) {
				return '!border !border-success';
			}

			return '';
		},
	},
	mounted() {
		if (this.doNotBlock) { this.validateField(); }
		this.setDisabled();
	},
	methods: {
		setDisabled() {
			this.isDisabled = this.doNotBlock ? false : (!!this.modelValue.value && !this.validationMessage);
		},
		onKeyUp() {
			this.$emit('dirty');
			this.modelValue.value = this.modelValue.value.replace(/\D/g, '');
		},
		onKeyDown(e: KeyboardEvent) {
			this.isChanging = true;

			if (this.modelValue.country !== 'PL') { return; }

			// Enable keyboard schortcuts for copy/paste and various actions
			if (e.ctrlKey) { return; }

			// Ignore non-numeric characters for NIP fields for non-polish numbers.
			const allowedKeys = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', 'Backspace', 'ArrowLeft', 'ArrowRight', 'Delete', 'Tab', 'Home', 'End'];
			if (!allowedKeys.includes(e.key)) {
				e.preventDefault(); // Prevent the default action of the keypress event
			}
		},

		onInput() {
			this.isChanging = false;
			this.modelValue.value = (this.modelValue.value || '').replace(/\D/g, '');
			this.$emit('dirty');
			this.validateField();
			this.$emit('update:modelValue', this.modelValue);
		},

		/**
		 * Wysyła żądanie do serwera o sprawdzenie poprawności wpisanych danych.
		 * @returns `true` jeżeli dane są poprawne.
		 */
		async validateField() {
			this.validationMessage = '';
			if (this.disableValidation) { return true; }

			const { modelValue: value } = this;
			if (value.isIndividual && !value.value) { return true; }
			if (this.doNotBlock && !value.value) { return true; }
			if (this.forceDisabled || this.isDisabled) { return true; }

			const rsvp = await serverFetch('/myoferteo/konto-json/validate-nip.ofer', {
				'idCustomer': this.userStore.customerId,
				'nip.country': value.country,
				'nip.value': value.value,
				'nip.isIndividual': value.isIndividual,
			}, 'POST');

			if (!rsvp.status) {
				this.validationMessage = this.validationMessages['NIP_ERROR'];
				return false;
			}

			const validationMessage = (rsvp.validationMessage || '') as NipValidationMessage;
			this.validationMessage = this.validationMessages[validationMessage] || '';

			// Jeżeli jesteśmy w trybie nieblokującym, tj. w edycji danych do faktury,
			// zignoruj komunikaty o pustym, bądź zduplikowanym NIPie.
			if (this.doNotBlock && validationMessage === 'NIP_DUPLICATED') {
				this.validationMessage = '';
				return true;
			}

			return !validationMessage;
		},

		async saveNipData() {
			const actionInputJSONString = JSON.stringify({
				object: 'nip',
				data: this.modelValue,
			});

			await serverFetch('/myoferteo/konto-json/update-profile.ofer', {
				object: 'nip',
				actionInputJSONString,
			}, 'POST');
		},
	},
};
</script>
